const normalizeThemeKey = (value) => String(value || '').trim().toLowerCase();
const isEnvTrue = (value) => normalizeThemeKey(value) === 'true';

const parseDiscountPercent = (value, fallback) => {
	const parsed = Number(value);
	if (!Number.isFinite(parsed)) return fallback;
	return Math.min(Math.max(parsed, 0), 95);
};

const parseEndsAt = (value) => {
	const raw = String(value || '').trim();
	if (!raw) return null;
	// Accept YYYY-MM-DD as end-of-day UTC-ish local date
	const normalized = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T23:59:59` : raw;
	const date = new Date(normalized);
	return Number.isFinite(date.getTime()) ? date.toISOString() : null;
};

const themeEnvConfig = {
	christmas: {
		enabled: isEnvTrue(process.env.REACT_APP_CHRISTMAS_MODE),
		code: (process.env.REACT_APP_CHRISTMAS_CODE || 'CHRISTMAS2025').trim(),
		discountPercent: parseDiscountPercent(process.env.REACT_APP_CHRISTMAS_DISCOUNT, 20),
		endsAt: parseEndsAt(process.env.REACT_APP_CHRISTMAS_ENDS),
	},
	halloween: {
		enabled: isEnvTrue(process.env.REACT_APP_HALLOWEEN_MODE),
		code: (process.env.REACT_APP_HALLOWEEN_CODE || 'HALLOWEEN2025').trim(),
		discountPercent: parseDiscountPercent(process.env.REACT_APP_HALLOWEEN_DISCOUNT, 15),
		endsAt: parseEndsAt(process.env.REACT_APP_HALLOWEEN_ENDS),
	},
	easter: {
		enabled: isEnvTrue(process.env.REACT_APP_EASTER_MODE),
		code: (process.env.REACT_APP_EASTER_CODE || 'EASTER2025').trim(),
		discountPercent: parseDiscountPercent(process.env.REACT_APP_EASTER_DISCOUNT, 15),
		endsAt: parseEndsAt(process.env.REACT_APP_EASTER_ENDS),
	},
};

// Prefer explicit single theme enum if still present, otherwise first enabled flag
const rawSeasonalTheme = normalizeThemeKey(process.env.REACT_APP_SEASONAL_THEME);

const resolveActiveThemeName = () => {
	if (['christmas', 'halloween', 'easter'].includes(rawSeasonalTheme)) {
		return rawSeasonalTheme;
	}
	if (rawSeasonalTheme === 'none' || rawSeasonalTheme === 'default' || rawSeasonalTheme === 'off' || rawSeasonalTheme === 'false') {
		return 'default';
	}
	if (themeEnvConfig.christmas.enabled) return 'christmas';
	if (themeEnvConfig.halloween.enabled) return 'halloween';
	if (themeEnvConfig.easter.enabled) return 'easter';
	return 'default';
};

const applyEnvOverrides = (theme) => {
	if (!theme || theme.name === 'default') return theme;
	const env = themeEnvConfig[theme.name];
	if (!env) return theme;

	const discountPercent = env.discountPercent;
	const discountMultiplier = discountPercent > 0 ? (1 - discountPercent / 100) : 1;
	const showDiscount = discountPercent > 0;
	const labelByTheme = {
		christmas: `${discountPercent}% Xmas Discount`,
		halloween: `${discountPercent}% Halloween Deal`,
		easter: `${discountPercent}% Easter Deal`,
	};
	const ribbonByTheme = {
		christmas: `${discountPercent}% Xmas`,
		halloween: `${discountPercent}% Off`,
		easter: `${discountPercent}% Easter`,
	};

	return {
		...theme,
		flags: {
			...theme.flags,
			showDiscount,
		},
		settings: {
			...theme.settings,
			pricing: {
				...theme.settings?.pricing,
				discountMultiplier,
				discountPercent,
			},
			campaign: {
				...theme.settings?.campaign,
				endsAt: env.endsAt || theme.settings?.campaign?.endsAt || null,
			},
		},
		copy: {
			...theme.copy,
			promoCode: env.code || theme.copy?.promoCode || '',
			discountLabel: labelByTheme[theme.name] || `${discountPercent}% Discount`,
			promoSaveText: `Save ${discountPercent}% on every product`,
			ribbonLabel: ribbonByTheme[theme.name] || `${discountPercent}% Off`,
		},
	};
};

const defaultTheme = {
	name: 'default',
	flags: {
		showDiscount: false,
	},
	settings: {
		pricing: {
			discountMultiplier: 1,
		},
		campaign: {
			endsAt: null,
		},
		tokens: {
			sliderAccent: '#6366F1',
			checkboxRing: 'focus:ring-indigo-200',
			tabDiscountBadge: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-700 dark:text-indigo-200',
		},
	},
	classes: {
		typography: {
			accent: 'text-indigo-600',
			accentHover: 'hover:text-indigo-600',
			accentUnderline: 'after:bg-indigo-600',
			accentOnDark: 'text-indigo-600 dark:text-indigo-500',
			price: 'text-indigo-600 dark:text-indigo-400',
			discountLabel: 'text-indigo-500 font-semibold',
			mapTitle: 'text-black dark:text-white',
			counter: 'text-slate-400 dark:text-white',
			feedbackHighlight: 'after:bg-indigo-600/30 text-indigo-600',
		},
		buttons: {
			primary: 'bg-indigo-600 hover:bg-indigo-700 border border-indigo-600 hover:border-indigo-700 text-white shadow-lg hover:shadow-indigo-600/20',
			cardPrimary: 'bg-indigo-600 hover:bg-indigo-500 border border-indigo-600 text-white',
			solidControl: 'bg-indigo-600 hover:bg-indigo-700 border border-indigo-600 text-white',
			compareActive: 'bg-indigo-600 text-white border-indigo-600',
			contact: 'bg-indigo-600 hover:bg-indigo-800 border border-indigo-600 text-white',
			buyNow: 'mt-4 w-full md:w-56 py-2 px-8 inline-block font-normal tracking-wide border align-middle duration-500 text-base text-center bg-indigo-600 hover:bg-indigo-700 border-indigo-600 hover:border-indigo-700 text-white rounded-md me-2 justify-center',
			outOfStock: 'mt-4 w-full md:w-56 py-2 px-8 inline-block font-normal tracking-wide bg-gray-300 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-md justify-center text-center opacity-60 cursor-not-allowed',
		},
		chips: {
			stripe: 'bg-indigo-500',
			badge: 'bg-indigo-600',
		},
		icons: {
			accent: 'text-indigo-500',
		},
		cards: {
			highlight: 'bg-white dark:bg-slate-900 border border-indigo-300 dark:border-indigo-500 ring-1 ring-indigo-300 dark:ring-indigo-500/40',
			planHover: 'hover:border-indigo-600',
			popularBadge: 'text-indigo-900 dark:text-white bg-amber-400 dark:bg-amber-500',
		},
		gradients: {
			primaryFrom: 'from-indigo-500',
			primaryTo: 'to-indigo-700',
		},
		decor: {
			softIconBg: 'bg-indigo-600/5 border border-indigo-600/10',
			iconHoverBg: 'group-hover:bg-indigo-600',
			photoAccent: 'bg-indigo-600/5',
			subtleBadge: 'bg-indigo-600/10 text-indigo-600',
			spinnerBorder: 'border-indigo-600',
			infoIcon: 'bg-indigo-600/5 text-indigo-600',
			hexagon: 'fill-indigo-600/5',
		},
		hero: {
			background: 'bg-indigo-600',
			badge: 'bg-indigo-600/10 dark:bg-indigo-600/20 text-indigo-600',
			title: 'text-indigo-600',
			underline: 'bg-indigo-600/30',
			primaryButton: 'bg-indigo-600 hover:bg-indigo-700 border border-indigo-600 hover:border-indigo-700 text-white shadow-lg hover:shadow-indigo-600/20',
			glow: 'from-indigo-600/20 to-indigo-600/20',
			iconBg: 'bg-indigo-600',
			spinner: 'after:bg-indigo-600/5',
			typeAccent: 'text-indigo-400',
			cta: 'py-3 px-6 inline-block font-semibold tracking-wide text-base text-center bg-indigo-600 hover:bg-indigo-700 border-indigo-600 text-white rounded-md shadow-2xl transition transform duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
			login: 'inline-flex items-center justify-center py-3 px-6 rounded-md border border-white/10 bg-transparent text-indigo-300 hover:bg-white/5 transition duration-200 transform hover:translate-x-1',
			promoCode: 'px-2 py-1 rounded-md bg-gradient-to-r from-indigo-600 to-indigo-500 text-white text-xs font-bold tracking-[0.08em]',
		},
		notifications: {
			background: 'bg-indigo-600',
			progress: 'bg-indigo-500',
		},
		graphics: {
			hexagon: 'fill-indigo-600/5',
		},
		effects: {
			hoverBorderAccent: 'hover:border-indigo-600',
		},
		controls: {
			focusAccent: 'focus:border-indigo-600 dark:focus:border-indigo-600',
		},
		tables: {
			compare: {
				light: 'bg-indigo-50',
				dark: 'bg-indigo-900/40',
				badgeLight: 'bg-indigo-100 text-indigo-700',
				badgeDark: 'bg-indigo-500/30 text-indigo-300',
			},
		},
		feedback: {
			linkAccent: 'text-indigo-600 hover:text-indigo-600 after:bg-indigo-600',
		},
		cart: {
			link: 'font-medium text-indigo-600 underline decoration-indigo-400/70 underline-offset-2 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300',
			linkSimple: 'text-indigo-600 dark:text-indigo-400 font-medium hover:underline',
			linkMuted: 'text-indigo-600 dark:text-indigo-300 underline decoration-indigo-400/60',
			linkLegal: 'text-indigo-600',
			focusRing: 'focus:ring-indigo-500',
			applyButton: 'bg-indigo-500 hover:bg-indigo-400',
			applyButtonDisabled: 'bg-indigo-300 cursor-not-allowed',
			softPanel: 'border border-indigo-200 dark:border-indigo-900 bg-indigo-50/70 dark:bg-slate-900/40',
			checkbox: 'peer-focus-visible:ring-indigo-400/60 peer-checked:bg-indigo-600 peer-checked:border-indigo-600',
			emptyIconWrap: 'border border-indigo-200/80 dark:border-indigo-500/20 bg-indigo-50 dark:bg-indigo-500/10',
			emptyIcon: 'text-indigo-600 dark:text-indigo-400',
			ctaButton: 'bg-indigo-600 text-white hover:bg-indigo-500',
			ctaButtonDisabled: 'disabled:bg-indigo-400',
			optionActive: 'border-gray-300 bg-gray-100 text-gray-900 shadow-sm dark:border-indigo-500/60 dark:bg-indigo-950/50 dark:text-indigo-100 dark:shadow-none',
			optionIdle: 'border-gray-300 bg-white text-gray-800 hover:border-gray-400 hover:bg-gray-50 dark:border-indigo-500/35 dark:bg-indigo-950/30 dark:text-indigo-200 dark:hover:border-indigo-400/50 dark:hover:bg-indigo-950/45',
			optionIcon: 'border-gray-200 bg-gray-50 text-gray-600 dark:border-indigo-400/20 dark:bg-indigo-400/15 dark:text-indigo-300',
			optionBadge: 'border-gray-200 bg-gray-50 text-gray-600 dark:border-indigo-500/30 dark:bg-indigo-900/40 dark:text-indigo-300',
			selectedChip: 'bg-indigo-500 text-white ring-2 ring-indigo-300',
			helperText: 'text-indigo-300',
			progressBar: 'bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-400',
			successRing: 'border-indigo-400/50',
			successGlow: 'bg-indigo-500/20',
			successIcon: 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30',
			stepBadge: 'border border-indigo-200 dark:border-indigo-500/40 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-300',
			toastLink: 'text-indigo-600 hover:text-indigo-500',
			toastLinkDark: 'text-indigo-400 hover:text-indigo-300',
			confettiColors: ['#6366f1', '#818cf8', '#38bdf8', '#22d3ee', '#34d399', '#a3e635', '#fbbf24', '#fb7185', '#c084fc', '#60a5fa'],
			burstColors: ['bg-indigo-400', 'bg-pink-400', 'bg-cyan-400', 'bg-amber-400', 'bg-emerald-400', 'bg-white'],
		},
	},
	components: {
		nav: {
			className: '',
			overlayActive: false,
			particles: false,
			particlePalette: null,
			particleVariant: 'snow',
		},
		footer: {
			socialAccent: 'border-gray-800 hover:border-indigo-600 dark:hover:border-indigo-600 hover:bg-indigo-600 dark:hover:bg-indigo-600 text-gray-200',
			background: 'bg-dark-footer',
			text: 'text-gray-200 dark:text-gray-200',
			link: 'text-gray-300 hover:text-gray-400',
			heading: 'text-gray-100',
			svgAccent: 'text-dark-footer',
			particles: false,
			particleColor: 'rgba(255,255,255,1)',
			abuseIcon: 'text-gray-400',
			abuseInput: 'bg-gray-800 border-0 text-gray-100 placeholder:text-gray-200',
			abuseCopyIdle: '#9CA3AF',
			abuseCopyActive: '#4CAF50',
			abuseCopyHover: 'text-gray-400 hover:text-gray-200',
			abuseBadgeBorder: 'border-t-[#007bff] border-l-[#007bff]',
			abuseBadgeBackground: '#3399ff linear-gradient(rgba(255,255,255,0), rgba(255,255,255,.3) 50%, rgba(0,0,0,.2) 51%, rgba(0,0,0,0))',
			whatsappHint: 'text-gray-400',
			borderTop: 'border-slate-800',
			showGreeting: false,
			greetingClass: 'text-gray-400',
		},
		landing: {
			showSeasonalHero: false,
			showPromoCode: false,
			showDiscountRibbon: false,
			showParallaxBand: false,
			heroAriaLabel: 'Trivox Hero',
			heroOverlay: 'linear-gradient(rgba(2,6,23,0.45), rgba(2,6,23,0.25))',
			assetKey: null,
			parallaxAssetKey: null,
			seasonalIcon: null,
		},
	},
	copy: {
		discountLabel: 'Limited Offer',
		pricingTitle: 'Choose Pricing Plan',
		pricingSubtitle: 'We have a suitable server for every budget!',
		heroSupport: 'VPS, Dedicated Servers and web hosting from Amsterdam with enterprise hardware, DDoS protection and fast connectivity. Prepaid, no contract.',
		promoCode: '',
		promoSaveText: '',
		ribbonLabel: '',
		holidayGreeting: '',
		parallaxTitle: '',
		parallaxSubtitle: '',
		heroCtaLabel: 'Get Started',
		heroLoginLabel: 'Login',
		heroLoginHint: 'to your Trivox account',
		countdownLabel: 'Offer ends in',
	},
};

const christmasTheme = {
	name: 'christmas',
	flags: {
		showDiscount: true,
	},
	settings: {
		pricing: {
			discountMultiplier: 0.8,
		},
		tokens: {
			sliderAccent: '#DC2626',
			checkboxRing: 'focus:ring-red-200',
			tabDiscountBadge: 'bg-red-100 text-red-700 dark:bg-red-700 dark:text-red-200',
		},
	},
	classes: {
		typography: {
			accent: 'text-red-600',
			accentHover: 'hover:text-red-600',
			accentUnderline: 'after:bg-red-600',
			accentOnDark: 'text-red-600 dark:text-red-500',
			price: 'text-red-600 dark:text-red-400',
			discountLabel: 'text-red-500 font-semibold',
			mapTitle: 'text-red-600 dark:text-red-500',
			counter: 'text-red-600 dark:text-red-500',
			feedbackHighlight: 'after:bg-red-600/30 text-red-600',
		},
		buttons: {
			primary: 'bg-red-600 hover:bg-red-700 border border-red-600 hover:border-red-700 text-white shadow-lg hover:shadow-red-600/20',
			cardPrimary: 'bg-red-600 hover:bg-red-500 border border-red-600 text-white',
			solidControl: 'bg-red-600 hover:bg-red-700 border border-red-600 text-white',
			compareActive: 'bg-red-600 text-white border-red-600',
			contact: 'bg-red-600 hover:bg-red-800 border border-red-600 text-white',
			buyNow: 'mt-4 w-full md:w-56 py-2 px-8 inline-block font-normal tracking-wide bg-red-600 hover:bg-red-700 border-red-600 hover:border-red-700 text-white rounded-md justify-center',
			outOfStock: 'mt-4 w-full md:w-56 py-2 px-8 inline-block font-normal tracking-wide bg-gray-400 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md justify-center text-center opacity-60 cursor-not-allowed',
		},
		chips: {
			stripe: 'bg-red-500',
			badge: 'bg-red-600',
		},
		icons: {
			accent: 'text-red-500',
		},
		cards: {
			highlight: 'bg-white dark:bg-slate-900 border border-red-300 dark:border-red-500 ring-1 ring-red-300 dark:ring-red-500/40',
			planHover: 'hover:border-red-600',
			popularBadge: 'text-white bg-emerald-500',
		},
		gradients: {
			primaryFrom: 'from-red-500',
			primaryTo: 'to-red-700',
		},
		decor: {
			softIconBg: 'bg-red-600/5 border border-red-600/10',
			iconHoverBg: 'group-hover:bg-red-600',
			photoAccent: 'bg-red-600/10',
			subtleBadge: 'bg-red-600/10 text-red-600',
			spinnerBorder: 'border-red-600',
			infoIcon: 'bg-red-600/5 text-red-600',
			hexagon: 'fill-red-600/5',
		},
		hero: {
			background: 'bg-red-600',
			badge: 'bg-red-600/10 dark:bg-red-600/20 text-red-600',
			title: 'text-red-600',
			underline: 'bg-red-600/30',
			primaryButton: 'bg-red-600 hover:bg-red-700 border border-red-600 hover:border-red-700 text-white shadow-lg hover:shadow-red-600/20',
			glow: 'from-red-600/20 to-red-500/20',
			iconBg: 'bg-red-600',
			spinner: 'after:bg-red-600/5',
			typeAccent: 'text-red-400',
			cta: 'py-3 px-6 inline-block font-semibold tracking-wide text-base text-center bg-red-600 hover:bg-red-700 border-red-600 text-white rounded-md shadow-2xl transition transform duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
			login: 'inline-flex items-center justify-center py-3 px-6 rounded-md border border-white/10 bg-transparent text-red-600 hover:bg-white/5 transition duration-200 transform hover:translate-x-1',
			promoCode: 'px-2 py-1 rounded-md bg-gradient-to-r from-red-600 to-red-500 text-white text-xs font-bold tracking-[0.08em]',
		},
		notifications: {
			background: 'bg-red-600',
			progress: 'bg-red-500',
		},
		graphics: {
			hexagon: 'fill-red-600/5',
		},
		effects: {
			hoverBorderAccent: 'hover:border-red-600',
		},
		controls: {
			focusAccent: 'focus:border-red-500 dark:focus:border-red-500',
		},
		tables: {
			compare: {
				light: 'bg-red-50',
				dark: 'bg-red-900/40',
				badgeLight: 'bg-red-100 text-red-700',
				badgeDark: 'bg-red-500/30 text-red-300',
			},
		},
		feedback: {
			linkAccent: 'text-red-600 hover:text-red-600 after:bg-red-600',
		},
		cart: {
			link: 'font-medium text-red-600 underline decoration-red-400/70 underline-offset-2 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300',
			linkSimple: 'text-red-600 dark:text-red-400 font-medium hover:underline',
			linkMuted: 'text-red-600 dark:text-red-300 underline decoration-red-400/60',
			linkLegal: 'text-red-600',
			focusRing: 'focus:ring-red-500',
			applyButton: 'bg-red-500 hover:bg-red-400',
			applyButtonDisabled: 'bg-red-300 cursor-not-allowed',
			softPanel: 'border border-red-200 dark:border-red-900 bg-red-50/70 dark:bg-slate-900/40',
			checkbox: 'peer-focus-visible:ring-red-400/60 peer-checked:bg-red-600 peer-checked:border-red-600',
			emptyIconWrap: 'border border-red-200/80 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10',
			emptyIcon: 'text-red-600 dark:text-red-400',
			ctaButton: 'bg-red-600 text-white hover:bg-red-500',
			ctaButtonDisabled: 'disabled:bg-red-400',
			optionActive: 'border-gray-300 bg-gray-100 text-gray-900 shadow-sm dark:border-red-500/60 dark:bg-red-950/50 dark:text-red-100 dark:shadow-none',
			optionIdle: 'border-gray-300 bg-white text-gray-800 hover:border-gray-400 hover:bg-gray-50 dark:border-red-500/35 dark:bg-red-950/30 dark:text-red-200 dark:hover:border-red-400/50 dark:hover:bg-red-950/45',
			optionIcon: 'border-gray-200 bg-gray-50 text-gray-600 dark:border-red-400/20 dark:bg-red-400/15 dark:text-red-300',
			optionBadge: 'border-gray-200 bg-gray-50 text-gray-600 dark:border-red-500/30 dark:bg-red-900/40 dark:text-red-300',
			selectedChip: 'bg-red-500 text-white ring-2 ring-red-300',
			helperText: 'text-red-300',
			progressBar: 'bg-gradient-to-r from-red-500 via-emerald-500 to-amber-400',
			successRing: 'border-red-400/50',
			successGlow: 'bg-red-500/20',
			successIcon: 'bg-red-600 text-white shadow-lg shadow-red-600/30',
			stepBadge: 'border border-red-200 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-300',
			toastLink: 'text-red-600 hover:text-red-500',
			toastLinkDark: 'text-red-400 hover:text-red-300',
			confettiColors: ['#dc2626', '#ef4444', '#22c55e', '#16a34a', '#fbbf24', '#f87171', '#86efac', '#fde68a', '#b91c1c', '#4ade80'],
			burstColors: ['bg-red-400', 'bg-emerald-400', 'bg-amber-400', 'bg-rose-400', 'bg-green-400', 'bg-white'],
		},
	},
	components: {
		nav: {
			className: 'christmas-nav',
			overlayActive: true,
			particles: true,
			particlePalette: ['rgba(255,255,255,0.92)', 'rgba(248,250,252,0.82)', 'rgba(226,232,240,0.7)'],
			particleVariant: 'snow',
			particleDensity: 68,
		},
		footer: {
			socialAccent: 'border-green-600 bg-green-600 text-white hover:bg-green-500 hover:border-green-500',
			background: 'bg-red-700',
			text: 'text-green-100',
			svgAccent: 'text-red-700',
			link: 'text-green-100 hover:text-green-200',
			heading: 'text-green-100',
			particles: true,
			particleColor: 'rgba(255,255,255,0.85)',
			particleCount: 12,
			abuseIcon: 'text-black',
			abuseInput: 'bg-green-500 border border-green-500 text-black placeholder:text-black',
			abuseCopyIdle: '#000000cc',
			abuseCopyActive: '#000000',
			abuseCopyHover: 'text-black hover:text-black/80',
			abuseBadgeBorder: 'border-t-[#22c55e] border-l-[#22c55e]',
			abuseBadgeBackground: '#22c55e linear-gradient(rgba(255,255,255,0), rgba(255,255,255,.3) 50%, rgba(0,0,0,.2) 51%, rgba(0,0,0,0))',
			whatsappHint: 'text-white',
			borderTop: 'border-red-600/40',
			showGreeting: true,
			greetingClass: 'text-green-100',
		},
		landing: {
			showSeasonalHero: true,
			showPromoCode: true,
			showDiscountRibbon: true,
			showParallaxBand: true,
			heroAriaLabel: 'Trivox Christmas Hero',
			heroOverlay: 'linear-gradient(rgba(2,6,23,0.45), rgba(2,6,23,0.25))',
			assetKey: 'christmas',
			parallaxAssetKey: 'christmasParallax',
			ribbonStops: ['#ef4444', '#f97316'],
			seasonalIcon: 'Gift',
			showLights: true,
			showOrnaments: true,
			showHolly: true,
			showSleigh: true,
			showGifts: true,
			showFrostEdge: true,
			showCandleGlow: true,
			showCardFrost: true,
			showHohoToast: true,
			showSnowflakeCursor: true,
		},
	},
	copy: {
		discountLabel: '20% Xmas Discount',
		pricingTitle: 'Christmas VPS Offers',
		pricingSubtitle: 'Limited Christmas discounts on our KVM servers. Secure your rate now.',
		heroSupport: 'KVM VPS and Dedicated Servers in Amsterdam with enterprise hardware, DDoS protection and fast connectivity. Prepaid Christmas discount included.',
		promoCode: 'CHRISTMAS2025',
		promoSaveText: 'Save 20% on every product',
		ribbonLabel: '20% Xmas',
		holidayGreeting: 'Happy Holidays from the Trivox team!',
		parallaxTitle: 'Holiday offers for a limited time',
		parallaxSubtitle: 'Discounts on VPS and Dedicated Servers with enterprise performance. While stocks last.',
		heroCtaLabel: 'Get Christmas Deal',
		heroLoginLabel: 'Login',
		heroLoginHint: 'to your Trivox account',
		countdownLabel: 'Holiday offer ends in',
		hohoTitle: 'Ho ho ho!',
		hohoMessage: 'Holiday deals are live. Grab your discount before Christmas is over.',
	},
};

const halloweenTheme = {
	name: 'halloween',
	flags: {
		showDiscount: true,
	},
	settings: {
		pricing: {
			discountMultiplier: 0.85,
		},
		tokens: {
			sliderAccent: '#F97316',
			checkboxRing: 'focus:ring-orange-200',
			tabDiscountBadge: 'bg-orange-100 text-orange-800 dark:bg-orange-700 dark:text-orange-100',
		},
	},
	classes: {
		typography: {
			accent: 'text-orange-600',
			accentHover: 'hover:text-orange-600',
			accentUnderline: 'after:bg-orange-600',
			accentOnDark: 'text-orange-600 dark:text-orange-400',
			price: 'text-orange-600 dark:text-orange-400',
			discountLabel: 'text-orange-500 font-semibold',
			mapTitle: 'text-orange-600 dark:text-orange-400',
			counter: 'text-orange-600 dark:text-orange-400',
			feedbackHighlight: 'after:bg-orange-600/30 text-orange-600',
		},
		buttons: {
			primary: 'bg-orange-600 hover:bg-orange-700 border border-orange-600 hover:border-orange-700 text-white shadow-lg hover:shadow-orange-600/20',
			cardPrimary: 'bg-orange-600 hover:bg-orange-500 border border-orange-600 text-white',
			solidControl: 'bg-orange-600 hover:bg-orange-700 border border-orange-600 text-white',
			compareActive: 'bg-orange-600 text-white border-orange-600',
			contact: 'bg-orange-600 hover:bg-orange-800 border border-orange-600 text-white',
			buyNow: 'mt-4 w-full md:w-56 py-2 px-8 inline-block font-normal tracking-wide bg-orange-600 hover:bg-orange-700 border-orange-600 hover:border-orange-700 text-white rounded-md justify-center',
			outOfStock: 'mt-4 w-full md:w-56 py-2 px-8 inline-block font-normal tracking-wide bg-gray-400 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md justify-center text-center opacity-60 cursor-not-allowed',
		},
		chips: {
			stripe: 'bg-orange-500',
			badge: 'bg-orange-600',
		},
		icons: {
			accent: 'text-orange-500',
		},
		cards: {
			highlight: 'bg-white dark:bg-slate-900 border border-orange-300 dark:border-orange-500 ring-1 ring-orange-300 dark:ring-orange-500/40',
			planHover: 'hover:border-orange-600',
			popularBadge: 'text-white bg-purple-700',
		},
		gradients: {
			primaryFrom: 'from-orange-500',
			primaryTo: 'to-purple-800',
		},
		decor: {
			softIconBg: 'bg-orange-600/5 border border-orange-600/10',
			iconHoverBg: 'group-hover:bg-orange-600',
			photoAccent: 'bg-orange-600/10',
			subtleBadge: 'bg-orange-600/10 text-orange-600',
			spinnerBorder: 'border-orange-600',
			infoIcon: 'bg-orange-600/5 text-orange-600',
			hexagon: 'fill-orange-600/5',
		},
		hero: {
			background: 'bg-orange-600',
			badge: 'bg-orange-600/10 dark:bg-orange-600/20 text-orange-600',
			title: 'text-orange-600',
			underline: 'bg-orange-600/30',
			primaryButton: 'bg-orange-600 hover:bg-orange-700 border border-orange-600 hover:border-orange-700 text-white shadow-lg hover:shadow-orange-600/20',
			glow: 'from-orange-600/20 to-purple-700/20',
			iconBg: 'bg-orange-600',
			spinner: 'after:bg-orange-600/5',
			typeAccent: 'text-orange-400',
			cta: 'py-3 px-6 inline-block font-semibold tracking-wide text-base text-center bg-orange-600 hover:bg-orange-700 border-orange-600 text-white rounded-md shadow-2xl transition transform duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2',
			login: 'inline-flex items-center justify-center py-3 px-6 rounded-md border border-white/10 bg-transparent text-orange-400 hover:bg-white/5 transition duration-200 transform hover:translate-x-1',
			promoCode: 'px-2 py-1 rounded-md bg-gradient-to-r from-orange-600 to-purple-700 text-white text-xs font-bold tracking-[0.08em]',
		},
		notifications: {
			background: 'bg-orange-600',
			progress: 'bg-orange-500',
		},
		graphics: {
			hexagon: 'fill-orange-600/5',
		},
		effects: {
			hoverBorderAccent: 'hover:border-orange-600',
		},
		controls: {
			focusAccent: 'focus:border-orange-500 dark:focus:border-orange-500',
		},
		tables: {
			compare: {
				light: 'bg-orange-50',
				dark: 'bg-orange-900/40',
				badgeLight: 'bg-orange-100 text-orange-800',
				badgeDark: 'bg-orange-500/30 text-orange-200',
			},
		},
		feedback: {
			linkAccent: 'text-orange-600 hover:text-orange-600 after:bg-orange-600',
		},
		cart: {
			link: 'font-medium text-orange-600 underline decoration-orange-400/70 underline-offset-2 hover:text-orange-500 dark:text-orange-400 dark:hover:text-orange-300',
			linkSimple: 'text-orange-600 dark:text-orange-400 font-medium hover:underline',
			linkMuted: 'text-orange-600 dark:text-orange-300 underline decoration-orange-400/60',
			linkLegal: 'text-orange-600',
			focusRing: 'focus:ring-orange-500',
			applyButton: 'bg-orange-500 hover:bg-orange-400',
			applyButtonDisabled: 'bg-orange-300 cursor-not-allowed',
			softPanel: 'border border-orange-200 dark:border-orange-900 bg-orange-50/70 dark:bg-slate-900/40',
			checkbox: 'peer-focus-visible:ring-orange-400/60 peer-checked:bg-orange-600 peer-checked:border-orange-600',
			emptyIconWrap: 'border border-orange-200/80 dark:border-orange-500/20 bg-orange-50 dark:bg-orange-500/10',
			emptyIcon: 'text-orange-600 dark:text-orange-400',
			ctaButton: 'bg-orange-600 text-white hover:bg-orange-500',
			ctaButtonDisabled: 'disabled:bg-orange-400',
			optionActive: 'border-gray-300 bg-gray-100 text-gray-900 shadow-sm dark:border-orange-500/60 dark:bg-orange-950/50 dark:text-orange-100 dark:shadow-none',
			optionIdle: 'border-gray-300 bg-white text-gray-800 hover:border-gray-400 hover:bg-gray-50 dark:border-orange-500/35 dark:bg-orange-950/30 dark:text-orange-200 dark:hover:border-orange-400/50 dark:hover:bg-orange-950/45',
			optionIcon: 'border-gray-200 bg-gray-50 text-gray-600 dark:border-orange-400/20 dark:bg-orange-400/15 dark:text-orange-300',
			optionBadge: 'border-gray-200 bg-gray-50 text-gray-600 dark:border-orange-500/30 dark:bg-orange-900/40 dark:text-orange-300',
			selectedChip: 'bg-orange-500 text-white ring-2 ring-orange-300',
			helperText: 'text-orange-300',
			progressBar: 'bg-gradient-to-r from-orange-500 via-amber-500 to-purple-600',
			successRing: 'border-orange-400/50',
			successGlow: 'bg-orange-500/20',
			successIcon: 'bg-orange-600 text-white shadow-lg shadow-orange-600/30',
			stepBadge: 'border border-orange-200 dark:border-orange-500/40 bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-300',
			toastLink: 'text-orange-600 hover:text-orange-500',
			toastLinkDark: 'text-orange-400 hover:text-orange-300',
			confettiColors: ['#f97316', '#fb923c', '#7c3aed', '#a855f7', '#eab308', '#fbbf24', '#f59e0b', '#c084fc', '#ea580c', '#fde047'],
			burstColors: ['bg-orange-400', 'bg-amber-400', 'bg-purple-400', 'bg-yellow-400', 'bg-violet-400', 'bg-white'],
		},
	},
	components: {
		nav: {
			className: 'halloween-nav',
			overlayActive: true,
			particles: true,
			particlePalette: ['rgba(15,23,42,0.88)', 'rgba(30,27,45,0.82)', 'rgba(68,64,60,0.72)', 'rgba(24,16,32,0.78)'],
			particleVariant: 'bats',
			particleDensity: 16,
		},
		footer: {
			socialAccent: 'border-orange-500 bg-orange-600 text-white hover:bg-orange-500 hover:border-orange-400',
			background: 'bg-[#1a0b2e]',
			text: 'text-orange-100',
			svgAccent: 'text-[#1a0b2e]',
			link: 'text-orange-100 hover:text-orange-200',
			heading: 'text-orange-50',
			particles: false,
			particleColor: 'rgba(249,115,22,0.85)',
			abuseIcon: 'text-black',
			abuseInput: 'bg-orange-500 border border-orange-500 text-black placeholder:text-black',
			abuseCopyIdle: '#000000cc',
			abuseCopyActive: '#000000',
			abuseCopyHover: 'text-black hover:text-black/80',
			abuseBadgeBorder: 'border-t-[#f97316] border-l-[#f97316]',
			abuseBadgeBackground: '#f97316 linear-gradient(rgba(255,255,255,0), rgba(255,255,255,.3) 50%, rgba(0,0,0,.2) 51%, rgba(0,0,0,0))',
			whatsappHint: 'text-white',
			borderTop: 'border-orange-500/40',
			showGreeting: true,
			greetingClass: 'text-orange-100',
		},
		landing: {
			showSeasonalHero: true,
			showPromoCode: true,
			showDiscountRibbon: true,
			showParallaxBand: true,
			heroAriaLabel: 'Trivox Halloween Hero',
			heroOverlay: 'linear-gradient(to bottom, rgba(10,4,20,0.72) 0%, rgba(26,11,46,0.28) 55%, rgba(26,11,46,0) 100%)',
			assetKey: 'halloween',
			parallaxAssetKey: 'halloweenParallax',
			ribbonStops: ['#f97316', '#7c3aed'],
			seasonalIcon: 'Moon',
			showFog: true,
			showSpider: true,
			showWeb: true,
			showPumpkinEyes: true,
			showMoon: false,
			showBatFlyby: true,
			showCandleGlow: true,
			showCardScare: true,
			showBooToast: true,
			showBatCursor: true,
		},
	},
	copy: {
		discountLabel: '15% Halloween Deal',
		pricingTitle: 'Halloween VPS Offers',
		pricingSubtitle: 'Limited Halloween discounts on our KVM servers. Secure your rate before it ends.',
		heroSupport: 'KVM VPS and Dedicated Servers in Amsterdam with enterprise hardware, DDoS protection and fast connectivity. Halloween prepaid discount included.',
		promoCode: 'HALLOWEEN2025',
		promoSaveText: 'Save 15% on every product',
		ribbonLabel: '15% Halloween',
		holidayGreeting: 'Happy Halloween from the Trivox team!',
		parallaxTitle: 'Halloween offers for a limited time',
		parallaxSubtitle: 'Halloween discounts on VPS and Dedicated Servers. While stocks last.',
		heroCtaLabel: 'Get Halloween Deal',
		heroLoginLabel: 'Login',
		heroLoginHint: 'to your Trivox account',
		countdownLabel: 'Halloween offer ends in',
		booTitle: 'Boo!',
		booMessage: 'Halloween deals are live. Grab your discount before they vanish.',
	},
};

const easterTheme = {
	name: 'easter',
	flags: {
		showDiscount: true,
	},
	settings: {
		pricing: {
			discountMultiplier: 0.85,
		},
		tokens: {
			sliderAccent: '#DB2777',
			checkboxRing: 'focus:ring-pink-200',
			tabDiscountBadge: 'bg-pink-100 text-pink-700 dark:bg-pink-700 dark:text-pink-100',
		},
	},
	classes: {
		typography: {
			accent: 'text-pink-600',
			accentHover: 'hover:text-pink-600',
			accentUnderline: 'after:bg-pink-600',
			accentOnDark: 'text-pink-600 dark:text-pink-400',
			price: 'text-pink-600 dark:text-pink-400',
			discountLabel: 'text-pink-500 font-semibold',
			mapTitle: 'text-pink-600 dark:text-pink-400',
			counter: 'text-pink-600 dark:text-pink-400',
			feedbackHighlight: 'after:bg-pink-600/30 text-pink-600',
		},
		buttons: {
			primary: 'bg-pink-600 hover:bg-pink-700 border border-pink-600 hover:border-pink-700 text-white shadow-lg hover:shadow-pink-600/20',
			cardPrimary: 'bg-pink-600 hover:bg-pink-500 border border-pink-600 text-white',
			solidControl: 'bg-pink-600 hover:bg-pink-700 border border-pink-600 text-white',
			compareActive: 'bg-pink-600 text-white border-pink-600',
			contact: 'bg-pink-600 hover:bg-pink-800 border border-pink-600 text-white',
			buyNow: 'mt-4 w-full md:w-56 py-2 px-8 inline-block font-normal tracking-wide bg-pink-600 hover:bg-pink-700 border-pink-600 hover:border-pink-700 text-white rounded-md justify-center',
			outOfStock: 'mt-4 w-full md:w-56 py-2 px-8 inline-block font-normal tracking-wide bg-gray-400 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md justify-center text-center opacity-60 cursor-not-allowed',
		},
		chips: {
			stripe: 'bg-pink-500',
			badge: 'bg-pink-600',
		},
		icons: {
			accent: 'text-pink-500',
		},
		cards: {
			highlight: 'bg-white dark:bg-slate-900 border border-pink-300 dark:border-pink-500 ring-1 ring-pink-300 dark:ring-pink-500/40',
			planHover: 'hover:border-pink-600',
			popularBadge: 'text-white bg-emerald-500',
		},
		gradients: {
			primaryFrom: 'from-pink-500',
			primaryTo: 'to-violet-600',
		},
		decor: {
			softIconBg: 'bg-pink-600/5 border border-pink-600/10',
			iconHoverBg: 'group-hover:bg-pink-600',
			photoAccent: 'bg-pink-600/10',
			subtleBadge: 'bg-pink-600/10 text-pink-600',
			spinnerBorder: 'border-pink-600',
			infoIcon: 'bg-pink-600/5 text-pink-600',
			hexagon: 'fill-pink-600/5',
		},
		hero: {
			background: 'bg-pink-600',
			badge: 'bg-pink-600/10 dark:bg-pink-600/20 text-pink-600',
			title: 'text-pink-600',
			underline: 'bg-pink-600/30',
			primaryButton: 'bg-pink-600 hover:bg-pink-700 border border-pink-600 hover:border-pink-700 text-white shadow-lg hover:shadow-pink-600/20',
			glow: 'from-pink-600/20 to-violet-500/20',
			iconBg: 'bg-pink-600',
			spinner: 'after:bg-pink-600/5',
			typeAccent: 'text-pink-300',
			cta: 'py-3 px-6 inline-block font-semibold tracking-wide text-base text-center bg-pink-600 hover:bg-pink-700 border-pink-600 text-white rounded-md shadow-2xl transition transform duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2',
			login: 'inline-flex items-center justify-center py-3 px-6 rounded-md border border-white/10 bg-transparent text-pink-300 hover:bg-white/5 transition duration-200 transform hover:translate-x-1',
			promoCode: 'px-2 py-1 rounded-md bg-gradient-to-r from-pink-600 to-violet-500 text-white text-xs font-bold tracking-[0.08em]',
		},
		notifications: {
			background: 'bg-pink-600',
			progress: 'bg-pink-500',
		},
		graphics: {
			hexagon: 'fill-pink-600/5',
		},
		effects: {
			hoverBorderAccent: 'hover:border-pink-600',
		},
		controls: {
			focusAccent: 'focus:border-pink-500 dark:focus:border-pink-500',
		},
		tables: {
			compare: {
				light: 'bg-pink-50',
				dark: 'bg-pink-900/40',
				badgeLight: 'bg-pink-100 text-pink-700',
				badgeDark: 'bg-pink-500/30 text-pink-200',
			},
		},
		feedback: {
			linkAccent: 'text-pink-600 hover:text-pink-600 after:bg-pink-600',
		},
		cart: {
			link: 'font-medium text-pink-600 underline decoration-pink-400/70 underline-offset-2 hover:text-pink-500 dark:text-pink-400 dark:hover:text-pink-300',
			linkSimple: 'text-pink-600 dark:text-pink-400 font-medium hover:underline',
			linkMuted: 'text-pink-600 dark:text-pink-300 underline decoration-pink-400/60',
			linkLegal: 'text-pink-600',
			focusRing: 'focus:ring-pink-500',
			applyButton: 'bg-pink-500 hover:bg-pink-400',
			applyButtonDisabled: 'bg-pink-300 cursor-not-allowed',
			softPanel: 'border border-pink-200 dark:border-pink-900 bg-pink-50/70 dark:bg-slate-900/40',
			checkbox: 'peer-focus-visible:ring-pink-400/60 peer-checked:bg-pink-600 peer-checked:border-pink-600',
			emptyIconWrap: 'border border-pink-200/80 dark:border-pink-500/20 bg-pink-50 dark:bg-pink-500/10',
			emptyIcon: 'text-pink-600 dark:text-pink-400',
			ctaButton: 'bg-pink-600 text-white hover:bg-pink-500',
			ctaButtonDisabled: 'disabled:bg-pink-400',
			optionActive: 'border-gray-300 bg-gray-100 text-gray-900 shadow-sm dark:border-pink-500/60 dark:bg-pink-950/50 dark:text-pink-100 dark:shadow-none',
			optionIdle: 'border-gray-300 bg-white text-gray-800 hover:border-gray-400 hover:bg-gray-50 dark:border-pink-500/35 dark:bg-pink-950/30 dark:text-pink-200 dark:hover:border-pink-400/50 dark:hover:bg-pink-950/45',
			optionIcon: 'border-gray-200 bg-gray-50 text-gray-600 dark:border-pink-400/20 dark:bg-pink-400/15 dark:text-pink-300',
			optionBadge: 'border-gray-200 bg-gray-50 text-gray-600 dark:border-pink-500/30 dark:bg-pink-900/40 dark:text-pink-300',
			selectedChip: 'bg-pink-500 text-white ring-2 ring-pink-300',
			helperText: 'text-pink-300',
			progressBar: 'bg-gradient-to-r from-pink-500 via-fuchsia-500 to-violet-500',
			successRing: 'border-pink-400/50',
			successGlow: 'bg-pink-500/20',
			successIcon: 'bg-pink-600 text-white shadow-lg shadow-pink-600/30',
			stepBadge: 'border border-pink-200 dark:border-pink-500/40 bg-pink-50 dark:bg-pink-500/10 text-pink-600 dark:text-pink-300',
			toastLink: 'text-pink-600 hover:text-pink-500',
			toastLinkDark: 'text-pink-400 hover:text-pink-300',
			confettiColors: ['#db2777', '#ec4899', '#a855f7', '#8b5cf6', '#f472b6', '#c084fc', '#f9a8d4', '#22d3ee', '#fbbf24', '#86efac'],
			burstColors: ['bg-pink-400', 'bg-fuchsia-400', 'bg-violet-400', 'bg-rose-400', 'bg-purple-400', 'bg-white'],
		},
	},
	components: {
		nav: {
			className: 'easter-nav',
			overlayActive: true,
			particles: true,
			particlePalette: ['rgba(244,114,182,0.9)', 'rgba(196,181,253,0.85)', 'rgba(167,243,208,0.8)'],
			particleVariant: 'petals',
		},
		footer: {
			socialAccent: 'border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-400 hover:border-emerald-400',
			background: 'bg-pink-700',
			text: 'text-pink-50',
			svgAccent: 'text-pink-700',
			link: 'text-pink-50 hover:text-pink-100',
			heading: 'text-white',
			particles: true,
			particleColor: 'rgba(244,114,182,0.9)',
			abuseIcon: 'text-black',
			abuseInput: 'bg-emerald-400 border border-emerald-400 text-black placeholder:text-black',
			abuseCopyIdle: '#000000cc',
			abuseCopyActive: '#000000',
			abuseCopyHover: 'text-black hover:text-black/80',
			abuseBadgeBorder: 'border-t-[#34d399] border-l-[#34d399]',
			abuseBadgeBackground: '#34d399 linear-gradient(rgba(255,255,255,0), rgba(255,255,255,.3) 50%, rgba(0,0,0,.2) 51%, rgba(0,0,0,0))',
			whatsappHint: 'text-white',
			borderTop: 'border-pink-400/40',
			showGreeting: true,
			greetingClass: 'text-pink-50',
		},
		landing: {
			showSeasonalHero: true,
			showPromoCode: true,
			showDiscountRibbon: true,
			showParallaxBand: true,
			heroAriaLabel: 'Trivox Easter Hero',
			heroOverlay: 'linear-gradient(rgba(15,8,30,0.42), rgba(76,29,149,0.22))',
			assetKey: 'easter',
			parallaxAssetKey: 'easterParallax',
			ribbonStops: ['#db2777', '#8b5cf6'],
			seasonalIcon: 'Sun',
		},
	},
	copy: {
		discountLabel: '15% Easter Deal',
		pricingTitle: 'Easter VPS Offers',
		pricingSubtitle: 'Limited spring discounts on our KVM servers. Secure your rate while it lasts.',
		heroSupport: 'KVM VPS and Dedicated Servers in Amsterdam with enterprise hardware, DDoS protection and fast connectivity. Spring prepaid discount included.',
		promoCode: 'EASTER2025',
		promoSaveText: 'Save 15% on every product',
		ribbonLabel: '15% Easter',
		holidayGreeting: 'Happy Easter from the Trivox team!',
		parallaxTitle: 'Spring offers for a limited time',
		parallaxSubtitle: 'Spring discounts on VPS and Dedicated Servers with the same enterprise performance. While stocks last.',
		heroCtaLabel: 'Get Spring Offer',
		heroLoginLabel: 'Login',
		heroLoginHint: 'to your Trivox account',
		countdownLabel: 'Spring offer ends in',
	},
};

const themeRegistry = {
	default: defaultTheme,
	christmas: christmasTheme,
	halloween: halloweenTheme,
	easter: easterTheme,
};

const activeThemeName = resolveActiveThemeName();
const seasonalTheme = applyEnvOverrides(themeRegistry[activeThemeName] || defaultTheme);

const isSeasonalModeEnabled = seasonalTheme.name !== 'default';
const isChristmasModeEnabled = seasonalTheme.name === 'christmas';
const isHalloweenModeEnabled = seasonalTheme.name === 'halloween';
const isEasterModeEnabled = seasonalTheme.name === 'easter';

const getValueFromPath = (root, path, fallback) => {
	if (!root || !path) return fallback;
	const segments = path.split('.');
	let current = root;
	for (const segment of segments) {
		if (current && Object.prototype.hasOwnProperty.call(current, segment)) {
			current = current[segment];
		} else {
			return fallback;
		}
	}
	return current ?? fallback;
};

export const getThemeClass = (path, fallback = '') => getValueFromPath(seasonalTheme.classes, path, fallback);
export const getComponentClass = (path, fallback = '') => getValueFromPath(seasonalTheme.components, path, fallback);
export const getThemeCopy = (path, fallback = '') => getValueFromPath(seasonalTheme.copy, path, fallback);
export const getThemeFlag = (path, fallback = false) => getValueFromPath(seasonalTheme.flags, path, fallback);
export const getThemeSetting = (path, fallback = null) => getValueFromPath(seasonalTheme.settings, path, fallback);
export const getSeasonalThemeName = () => seasonalTheme.name;
export const isTheme = (name) => seasonalTheme.name === name;

/**
 * Resolve seasonal marketing copy from welcome.json (i18n-first).
 * Theme-specific keys live under seasonal.themes.{name}.*; default theme uses seasonal.*.
 */
export const resolveSeasonalCopy = (t, key, options = {}) => {
	const { defaultValue = '', ...rest } = options;
	const enFallback = getThemeCopy(key, defaultValue);
	const percent = getThemeSetting('pricing.discountPercent', undefined);
	const opts = {
		ns: 'welcome',
		percent,
		...rest,
		defaultValue: enFallback,
	};
	if (seasonalTheme.name !== 'default') {
		return t(`seasonal.themes.${seasonalTheme.name}.${key}`, opts);
	}
	return t(`seasonal.${key}`, opts);
};

/**
 * Backward compatible:
 * - selectSeasonalValue(defaultValue, christmasValue)
 * - selectSeasonalValue({ default, christmas, halloween, easter })
 */
export const selectSeasonalValue = (defaultOrMap, christmasValue) => {
	if (defaultOrMap && typeof defaultOrMap === 'object' && !Array.isArray(defaultOrMap)) {
		const map = defaultOrMap;
		if (Object.prototype.hasOwnProperty.call(map, seasonalTheme.name)) {
			return map[seasonalTheme.name];
		}
		if (isSeasonalModeEnabled && Object.prototype.hasOwnProperty.call(map, 'seasonal')) {
			return map.seasonal;
		}
		return map.default;
	}
	return isChristmasModeEnabled ? (christmasValue ?? defaultOrMap) : defaultOrMap;
};

export {
	isChristmasModeEnabled,
	isHalloweenModeEnabled,
	isEasterModeEnabled,
	isSeasonalModeEnabled,
	seasonalTheme,
	themeRegistry,
};
